import { Link } from "react-router-dom";
import type { ServicesContent } from "../../types/page-content";

interface Props {
  content: ServicesContent;
  onEstimateClick: () => void;
  sectionNumber?: string;
}

export default function ServicesSection({ content, onEstimateClick, sectionNumber = "02" }: Props) {
  return (
    <section className="bg-[#111111] py-28 lg:py-40 relative overflow-hidden">
      <div className="absolute right-6 top-10 hidden xl:block">
        <span className="font-heading text-white/[0.03] text-[180px] leading-none select-none">{sectionNumber}</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-14">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-0.5 bg-safety-500" />
              <span className="font-body text-safety-500 text-xs uppercase tracking-widest2 font-700">{content.eyebrow}</span>
            </div>
            <h2 className="font-heading text-white text-6xl lg:text-8xl tracking-display leading-none">
              {content.heading}
            </h2>
          </div>
          <p className="font-body text-asphalt-300 text-sm leading-relaxed max-w-md lg:text-right">
            {content.subheading}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-asphalt-600">
          {content.items.map((service) => (
            <Link
              key={service.title}
              to={service.href}
              className="group bg-[#191919] hover:bg-safety-500 p-8 lg:p-10 flex flex-col gap-6 transition-all duration-300 cursor-pointer relative overflow-hidden"
            >
              <div className="absolute top-6 right-6 font-heading text-white/[0.06] group-hover:text-asphalt-950/10 text-5xl leading-none select-none transition-colors">
                {service.number}
              </div>
              <div className="w-12 h-12 flex items-center justify-center border border-safety-500 group-hover:border-asphalt-950/30 transition-colors">
                <i className={`${service.icon} text-safety-500 group-hover:text-asphalt-950 text-xl transition-colors`} />
              </div>
              <div>
                <h3 className="font-heading text-white group-hover:text-asphalt-950 text-3xl tracking-display mb-3 transition-colors">
                  {service.title}
                </h3>
                <p className="font-body text-asphalt-300 group-hover:text-asphalt-800 text-sm leading-relaxed transition-colors">
                  {service.description}
                </p>
              </div>
              <div className="flex items-center gap-2 text-safety-500 group-hover:text-asphalt-950 font-body font-700 text-xs uppercase tracking-widest mt-auto transition-colors">
                Learn More
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        {/* Before / After strip */}
        <div className="mt-16 grid grid-cols-2 gap-px bg-asphalt-600">
          <div className="relative overflow-hidden">
            <img
              src={content.beforeAfterBefore}
              alt="Before asphalt repair — cracked surface"
              className="w-full h-64 object-cover object-top filter brightness-75"
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute bottom-0 left-0 right-0 px-6 py-5 bg-gradient-to-t from-black">
              <span className="font-heading text-white/60 text-xs uppercase tracking-widest">Before</span>
              <p className="font-heading text-white text-2xl tracking-display">Cracked &amp; Deteriorated</p>
            </div>
          </div>
          <div className="relative overflow-hidden">
            <img
              src={content.beforeAfterAfter}
              alt="After asphalt paving — smooth new surface"
              className="w-full h-64 object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/20" />
            <div className="absolute bottom-0 left-0 right-0 px-6 py-5 bg-gradient-to-t from-black">
              <span className="font-heading text-safety-500 text-xs uppercase tracking-widest">After</span>
              <p className="font-heading text-white text-2xl tracking-display">Smooth &amp; Professional</p>
            </div>
          </div>
        </div>

        {/* Mid CTA */}
        <div className="mt-16 border border-asphalt-600 p-8 lg:p-12 flex flex-col lg:flex-row items-center justify-between gap-6 bg-[#191919]">
          <div>
            <p className="font-body text-safety-500 text-xs uppercase tracking-widest font-700 mb-2">Free No-Obligation Quote</p>
            <h3 className="font-heading text-white text-4xl lg:text-5xl tracking-display">{content.ctaHeading}</h3>
          </div>
          <div className="flex gap-4 flex-shrink-0">
            <button
              onClick={onEstimateClick}
              className="bg-safety-500 text-asphalt-950 font-body font-700 text-xs uppercase tracking-widest px-8 py-4 hover:bg-safety-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Estimate
            </button>
            <a
              href="tel:5558007283"
              className="border border-asphalt-500 text-asphalt-200 font-body font-700 text-xs uppercase tracking-widest px-8 py-4 hover:border-safety-500 hover:text-safety-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
